import { Box, Stack, Typography } from "@mui/material";
import ScoutIcon from "components/ScoutIcon";

import type { ScoutAttachment } from "../scoutSubmitAttachments";

import { SCOUT_NEW_CHAT_TOP_OFFSET_PX, SCOUT_SCROLLBAR_GUTTER_PX } from "../scoutLayout";
import { ScoutChatDropzone } from "./ScoutChatDropzone";
import { ScoutInput, type ScoutSubmitHandler } from "./ScoutInput";
import { ScoutSupportChatLink } from "./ScoutSupportChatLink";
import { SuggestionMenu } from "./SuggestionMenu";

type NewChatProps = {
  tenantId?: string;
  onSubmit: ScoutSubmitHandler;
  onAbort: () => void;
  isSending: boolean;
  isAborting: boolean;
  onAttachmentsUploaded?: (attachments: ScoutAttachment[]) => void;
  onAttachmentRemoved?: (privateDocumentId: string) => void | Promise<void>;
  hasScoutProAccess?: boolean;
};

function NewChatHero({
  tenantId,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  hasScoutProAccess,
}: Required<Pick<NewChatProps, "onSubmit" | "onAbort" | "isSending" | "isAborting">> &
  Pick<
    NewChatProps,
    "tenantId" | "onAttachmentsUploaded" | "onAttachmentRemoved" | "hasScoutProAccess"
  >) {
  return (
    <Stack
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        px: 2,
        // Pin the hero near the top (not vertically centered). Clears the
        // floating header and matches the empty-state Figma offset.
        pt: `${SCOUT_NEW_CHAT_TOP_OFFSET_PX}px`,
        pb: { xs: 12, sm: 16 },
        gap: 5,
      }}
    >
      <ScoutIcon sx={{ width: 80, height: 80 }} />
      <Typography variant="h6" textAlign="center">
        Hey there! I'm Scout, your Encamp guide. How can I help you today?
      </Typography>

      <ScoutInput
        tenantId={tenantId}
        conversationId={undefined}
        onSubmit={onSubmit}
        onAbort={onAbort}
        isSending={isSending}
        isAborting={isAborting}
        onAttachmentsUploaded={onAttachmentsUploaded}
        onAttachmentRemoved={onAttachmentRemoved}
        placeholder="Check regulations, help me understand compliance requirements..."
        compactPlaceholder="Ask Scout a question…"
        hasScoutProAccess={hasScoutProAccess}
      />

      <SuggestionMenu onPick={(prompt) => onSubmit(prompt)} disabled={isSending} />
    </Stack>
  );
}

export function NewChat({
  tenantId,
  onSubmit,
  onAbort,
  isSending,
  isAborting,
  onAttachmentsUploaded,
  onAttachmentRemoved,
  hasScoutProAccess = true,
}: NewChatProps) {
  return (
    <ScoutChatDropzone
      sx={{
        position: "relative",
        flex: 1,
        alignSelf: "stretch",
        width: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Full-height scroll surface so the scrollbar bleeds to the bottom edge
          behind the overlaid support footer (same pattern as ChatChrome). */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          minHeight: 0,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <NewChatHero
          tenantId={tenantId}
          onSubmit={onSubmit}
          onAbort={onAbort}
          isSending={isSending}
          isAborting={isAborting}
          onAttachmentsUploaded={onAttachmentsUploaded}
          onAttachmentRemoved={onAttachmentRemoved}
          hasScoutProAccess={hasScoutProAccess}
        />
      </Box>

      <Box
        data-scout-support-footer=""
        sx={{
          position: "absolute",
          left: 0,
          // Stop short of the scrollbar so the track stays visible to the bottom.
          right: `${SCOUT_SCROLLBAR_GUTTER_PX}px`,
          bottom: 0,
          zIndex: 2,
          display: "flex",
          justifyContent: "center",
          // 36px below the link so it reads clearly off the page edge.
          pb: 4.5,
          px: 2,
          bgcolor: "scoutCanvas",
          pointerEvents: "none",
          "& > *": { pointerEvents: "auto" },
        }}
      >
        <ScoutSupportChatLink />
      </Box>
    </ScoutChatDropzone>
  );
}
